import React from 'react';

/**
 * Component that renders the terms and conditions content for expert users.
 * Displays the complete Azymmetric Tyr Platform Expert Terms and Conditions Agreement.
 */
export const ExpertTermsContent: React.FC = () => {
  return (
    <div className="max-w-none space-y-6 text-sm">
      <h2 className="text-xl font-bold text-center">AZYMMETRIC TYR PLATFORM EXPERT TERMS AND CONDITIONS AGREEMENT</h2>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">1. ACCEPTANCE OF TERMS AND EXPERT DESIGNATION</h3>
        <p>
          By clicking "I Agree" and accessing or using the Azymmetric Tyr platform as an expert contributor, you
          ("Expert," "you," or "your") acknowledge that you have read, understood, and agree to be bound by these Expert
          Terms and Conditions ("Expert Terms") and all applicable laws and regulations. This agreement is effective as
          of the date you first access the Tyr platform as an expert and constitutes a legally binding contract between
          you and Azymmetric, Inc., a Texas C-Corporation ("Azymmetric," "we," "us," or "our") organized under the Texas
          Business Organizations Code.
        </p>
        <p>
          You acknowledge that you have also reviewed and understand the Terms and Conditions for Technology Companies
          using the Tyr platform, and that your expert services are provided within the framework of those terms. If you
          do not agree to these Expert Terms, you must not access or use the Azymmetric Tyr platform as an expert
          contributor.
        </p>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">2. CORPORATE STRUCTURE AND GOVERNANCE</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">2.1 Texas C-Corporation Compliance</h4>
          <p>
            Azymmetric operates as a Texas C-Corporation in compliance with the Texas Business Organizations Code and
            maintains all required corporate governance standards including board of directors oversight, corporate
            bylaws, and regulatory compliance. As a C-Corporation, Azymmetric provides limited liability protection to
            its shareholders while maintaining separate legal entity status.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">2.2 Corporate Governance and Expert Relations</h4>
          <p>
            Expert services are provided to a properly formed and maintained Texas corporation that adheres to
            applicable corporate governance requirements including the business judgment rule protections for officers
            and directors as codified in Texas law. Experts acknowledge that they are contracting with a legitimate
            corporate entity subject to Texas corporate law and regulatory oversight.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">3. EXPERT QUALIFICATIONS AND REPRESENTATIONS</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">3.1 Allied Nation Military Veteran Expert Qualifications</h4>
          <p>
            If you are providing services as a military veteran expert, you represent and warrant that you possess
            qualifying military service from one of the recognized allied nations with appropriate discharge status or
            service record. You further represent that you have relevant operational experience applicable to defense
            technology assessments and maintain current knowledge of military systems, requirements, and operational
            environments within allied nation frameworks.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">3.2 Technology Expert Qualifications</h4>
          <p>
            If you are providing services as a technology expert, you represent and warrant that you possess recognized
            credentials, certifications, or demonstrated expertise in your specified technology domain, substantial
            professional experience in technology development, implementation, or evaluation, and competence in
            Technology Readiness Level (TRL) and Manufacturing Readiness Level (MRL) assessment methodologies.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">3.3 Prohibited Affiliations and Security Requirements</h4>
          <p className="font-semibold">You represent and warrant that you are NOT and have never been:</p>
          <ul className="list-disc space-y-1 pl-6">
            <li>A member of the Chinese Communist Party (CCP) or any affiliated organization</li>
            <li>
              Affiliated with or employed by any foreign adversary nation as defined by U.S. regulations, including the
              People's Republic of China, Republic of Cuba, Islamic Republic of Iran, Democratic People's Republic of
              Korea, Russian Federation, or Venezuelan Maduro Regime
            </li>
            <li>
              Associated with non-state actors engaged in hostile activities against allied nations, including terrorist
              organizations, private military contractors operating against allied interests, or other malicious
              non-state entities
            </li>
            <li>
              Subject to foreign influence operations or required to provide assistance to hostile intelligence services
              under laws such as China's National Intelligence Law
            </li>
          </ul>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">3.4 Truthfulness and Accuracy Requirements</h4>
          <p>
            You represent and warrant that all information provided regarding your qualifications, experience,
            expertise, and security background is true, accurate, and complete. Any misrepresentation of your
            credentials, experience, expertise, or prohibited affiliations constitutes a material breach of these Expert
            Terms and may result in immediate termination and legal liability.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">4. EXPERT SERVICES AND OBLIGATIONS</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">4.1 Scope of Expert Services</h4>
          <p>
            As an expert on the Tyr platform, you agree to provide professional analysis and recommendations based on
            your documented expertise, technology-mission fit assessments for defense and technology applications from
            allied nation perspectives, independent expert opinions on TRL and MRL evaluations, verification and
            validation services for AI-generated content when requested, and collaborative input with other experts and
            AI systems as part of the platform's integrated approach.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">4.2 Professional Standards and Duty of Care</h4>
          <p>
            You agree to exercise reasonable skill, care, and professional judgment in accordance with the standards
            expected of a competent, qualified, and experienced professional in your field and within the context of
            allied nation security and technology frameworks. You shall perform all expert services in a professional
            and ethical manner consistent with industry best practices and allied nation security protocols.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">4.3 Truth and Competence Requirements</h4>
          <p>
            You must provide truthful, accurate analysis based solely on your genuine expertise and professional
            judgment. If you do not possess sufficient knowledge or expertise to address a specific question or
            assessment, you must refrain from providing an opinion rather than speculate or provide uninformed guidance.
            You shall make your best professional attempt to answer questions within your area of expertise while
            clearly identifying the limitations of your analysis.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">5. INFORMATION TRANSMISSION AND CONFIDENTIALITY RESTRICTIONS</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">5.1 Strict Information Transmission Prohibition</h4>
          <p>
            No information about technology companies, their data, assessments, or any content submitted to or generated
            by the Tyr platform shall be transmitted, disclosed, or shared outside the platform without the explicit
            written consent of both Azymmetric and the relevant technology company. This prohibition applies to all
            forms of communication including but not limited to verbal discussions, written reports, electronic
            communications, presentations, or any other means of information transfer.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">5.2 Platform-Only Information Use</h4>
          <p>
            All information accessed through the Tyr platform, including company assessments, technology details, expert
            analysis, and AI-generated content, must remain within the platform ecosystem unless specifically authorized
            for external use through proper consent mechanisms. You acknowledge that unauthorized information
            transmission constitutes a material breach of these Expert Terms and may result in legal liability.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">5.3 Consent Requirements for Information Sharing</h4>
          <p>
            Any sharing of platform information outside the Tyr ecosystem requires documented consent from both
            Azymmetric as the platform operator and the technology company whose information would be disclosed. Such
            consent must be obtained in writing and specify the exact information to be shared, the recipients, and the
            purpose of the disclosure.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">5.4 Third-Party Information Protection</h4>
          <p>
            You acknowledge that technology companies using the Tyr platform rely on information confidentiality and
            that unauthorized disclosure could harm their competitive position, violate their intellectual property
            rights, or compromise their business operations. You agree to implement appropriate safeguards to protect
            all platform information from inadvertent or unauthorized disclosure.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">6. AI-ENHANCED EXPERT SERVICES AND VERIFICATION</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">6.1 AI-Assisted Expert Analysis</h4>
          <p>
            The Tyr platform integrates artificial intelligence systems with human expert analysis to provide enhanced
            assessments. You acknowledge that future platform versions may provide AI-generated responses that require
            expert verification, validation, or enhancement. You agree to participate in AI verification processes when
            requested and qualified to do so.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">6.2 Expert Verification of AI Content</h4>
          <p>
            When reviewing AI-generated content, you must apply your professional judgment to evaluate the accuracy and
            relevance of AI outputs, identify any errors, biases, or limitations in AI-generated analysis, provide clear
            recommendations regarding the reliability of AI-generated content, and distinguish between AI-generated
            insights and your own professional analysis.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">6.3 Collaborative AI-Human Analysis</h4>
          <p>
            You acknowledge that the platform's strength lies in combining AI capabilities with human expertise from
            allied nation perspectives. You agree to work collaboratively with AI systems and other experts to provide
            comprehensive analysis that leverages both technological capabilities and human judgment within the
            framework of allied nation security and technology assessment standards.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">7. INTELLECTUAL PROPERTY AND DATA OWNERSHIP</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">7.1 Azymmetric Ownership of Platform Content</h4>
          <p>
            Azymmetric owns and retains all rights, title, and interest in and to all data, analysis, insights,
            recommendations, and intellectual property created, generated, or contributed through the Tyr platform. This
            includes all expert analysis, recommendations, reports, and any derivative works created from expert
            contributions.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">7.2 Reusable Expert Content</h4>
          <p>
            You acknowledge that your expert analysis, recommendations, and insights may be reused by Azymmetric and
            made available to other experts and users of the Tyr platform. You grant Azymmetric a perpetual,
            irrevocable, worldwide, royalty-free license to use, modify, adapt, and distribute your expert contributions
            for platform operations and improvement.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">7.3 Expert Methodology and Knowledge Retention</h4>
          <p>
            While Azymmetric owns content created through the platform, you retain ownership of your pre-existing
            professional knowledge, methodologies, and general expertise that you possessed prior to using the platform.
            However, any specific applications of your expertise within the Tyr platform context become part of
            Azymmetric's intellectual property.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">8. LIABILITY FOR MISREPRESENTATION AND PROFESSIONAL NEGLIGENCE</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">8.1 Liability for Misrepresentation of Expertise</h4>
          <p>
            You shall be solely liable for any misrepresentation of your qualifications, credentials, expertise, or
            prohibited affiliations. If you falsely represent your professional background, experience, capabilities, or
            security clearance status, you may be subject to claims for fraudulent misrepresentation, negligent
            misrepresentation, or professional negligence.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">8.2 Professional Malpractice Liability</h4>
          <p>
            You acknowledge that providing expert analysis and recommendations creates potential professional liability
            exposure. You may be held liable for professional malpractice if your expert services fall below the
            standard of care expected from a competent professional in your field.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">8.3 Liability for Misleading Advice</h4>
          <p>
            You shall be responsible for providing misleading, inaccurate, or negligent advice or recommendations. If
            your expert analysis contains material errors, omissions, or misstatements that cause harm to technology
            companies or other platform users, you may be held liable for resulting damages.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">8.4 Integration with Technology Company Terms</h4>
          <p>
            Your liability as an expert operates in conjunction with the Terms and Conditions for Technology Companies
            using the Tyr platform. Technology companies using the platform are required to provide truthful information
            and are liable for their own misrepresentations, while you remain liable for the accuracy and quality of
            your expert analysis based on the information provided.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">9. INDEMNIFICATION AND INSURANCE REQUIREMENTS</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">9.1 Expert Indemnification Obligations</h4>
          <p>
            You agree to indemnify and hold harmless Azymmetric from any claims, damages, losses, or expenses arising
            from misrepresentation of your qualifications or expertise, negligent or substandard expert services, breach
            of your professional duties or obligations, violations of applicable laws or regulations in providing expert
            services, or unauthorized disclosure of platform information.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">9.2 Professional Liability Insurance</h4>
          <p>
            You are strongly encouraged to maintain professional liability insurance adequate to cover potential claims
            arising from your expert services. While not required by Texas state law for consultants, professional
            liability coverage protects both you and platform users from financial exposure related to professional
            errors or omissions.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">10. TEXAS LAW COMPLIANCE AND PROFESSIONAL STANDARDS</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">10.1 Governing Law</h4>
          <p>
            These Expert Terms are governed by and construed in accordance with the laws of the State of Texas, without
            regard to conflict of law principles. Any disputes arising under these Expert Terms shall be subject to the
            exclusive jurisdiction of the courts of Texas.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">10.2 Professional Standards Compliance</h4>
          <p>
            You agree to comply with all applicable professional standards, codes of conduct, and ethical requirements
            governing your profession or area of expertise. This includes maintaining appropriate professional
            certifications and continuing education as required in your field.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">10.3 Texas Professional Services Requirements</h4>
          <p>
            Your expert services shall comply with applicable Texas professional services requirements, including
            standards for professional competence and ethical conduct. You acknowledge that professional liability may
            arise under both contract and tort law principles.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">11. TERMINATION AND SURVIVAL</h3>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">11.1 Termination Rights</h4>
          <p>
            Either party may terminate your expert access to the Tyr platform with thirty (30) days written notice.
            Azymmetric may terminate your expert access immediately for breach of these Expert Terms, misrepresentation
            of qualifications, professional misconduct, or violation of information transmission restrictions.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">11.2 Survival of Key Provisions</h4>
          <p>
            The following provisions shall survive termination of these Expert Terms: intellectual property and data
            ownership rights, liability for misrepresentation and professional negligence, indemnification obligations,
            information transmission restrictions, limitations of liability, and governing law and dispute resolution.
          </p>
        </div>
        <div className="space-y-1">
          <h4 className="text-sm font-semibold">11.3 Post-Termination Obligations</h4>
          <p>
            Upon termination, you shall cease providing expert services through the platform and continue to maintain
            confidentiality of all platform information. Azymmetric may continue to use your previously contributed
            expert analysis and recommendations in accordance with the intellectual property provisions.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h3 className="text-base font-semibold">12. FINAL ACKNOWLEDGMENT AND ACCEPTANCE</h3>
        <p>
          By clicking "I Agree," you acknowledge that you have read, understood, and agree to be bound by these Expert
          Terms and Conditions. You specifically acknowledge understanding that:
        </p>
        <ul className="list-disc space-y-1 pl-6">
          <li>You are liable for misrepresentation of your qualifications, expertise, or prohibited affiliations</li>
          <li>You may be held responsible for providing misleading or negligent advice</li>
          <li>Your expert contributions become intellectual property of Azymmetric</li>
          <li>You must be truthful and refrain from answering questions outside your expertise</li>
          <li>No platform information may be transmitted outside without proper consent</li>
          <li>You cannot be affiliated with the CCP, foreign adversary nations, or hostile non-state actors</li>
          <li>Future platform versions may require AI verification and collaboration</li>
          <li>These terms operate in conjunction with the Technology Company Terms and Conditions</li>
        </ul>
        <p>
          This agreement is effective immediately upon your acceptance and continues until terminated in accordance with
          these Expert Terms.
        </p>
      </section>

      <div className="space-y-1 text-xs text-gray-500">
        <p>Last Updated: 15 June 2025</p>
        <p>Azymmetric, Inc.</p>
        <p>Texas C-Corporation</p>
        <p>Governed by Texas Law</p>
        <p>Tyr Platform Expert Terms and Conditions</p>
      </div>

      <hr />

      <section className="space-y-4">
        <h4 className="text-sm font-semibold">Allied Nations Reference</h4>
        <p className="text-xs">Recognized Allied Nations for Military Veteran Expert Qualifications Include:</p>
        <ul className="list-disc space-y-1 pl-6 text-xs">
          <li>
            <strong>NATO Members (32 countries as of 2025):</strong> Albania, Belgium, Bulgaria, Canada, Croatia, Czech
            Republic, Denmark, Estonia, Finland, France, Germany, Greece, Hungary, Iceland, Italy, Latvia, Lithuania,
            Luxembourg, Montenegro, Netherlands, North Macedonia, Norway, Poland, Portugal, Romania, Slovakia, Slovenia,
            Spain, Sweden, Turkey, United Kingdom, and United States.
          </li>
          <li>
            <strong>Five Eyes Intelligence Alliance:</strong> Australia, Canada, New Zealand, United Kingdom, and United
            States.
          </li>
          <li>
            <strong>AUKUS Security Partnership:</strong> Australia, United Kingdom, and United States.
          </li>
          <li>
            <strong>Quadrilateral Security Dialogue (Quad):</strong> Australia, India, Japan, and United States.
          </li>
          <li>
            <strong>Major Non-NATO Allies (MNNAs):</strong> Including but not limited to Argentina, Australia, Bahrain,
            Brazil, Colombia, Egypt, Israel, Japan, Jordan, Kenya, Kuwait, Morocco, New Zealand, Pakistan, Philippines,
            Qatar, South Korea, Taiwan, Thailand, and Tunisia.
          </li>
          <li>
            <strong>Additional Strategic Partners:</strong> Countries with formal defense cooperation agreements, mutual
            defense treaties, or recognized security partnerships with the United States and allied nations may qualify
            subject to verification of military service records and security clearance requirements.
          </li>
        </ul>
      </section>
    </div>
  );
};
